import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import * as signalR from '@microsoft/signalr';

/**
 * Notification shape matching the old JsonRpcNotification for backward compatibility.
 */
interface Notification {
    jsonrpc: '2.0';
    method: string;
    params?: any;
}

/**
 * SignalR-based replacement for the original JSON-RPC WebSocket service.
 * Maintains the same public API (connect, disconnect, sendRequest, notifications$)
 * so ApiService requires minimal changes.
 */
@Injectable({
    providedIn: 'root',
})
export class JsonRpcWebSocketService implements OnDestroy {
    private connection: signalR.HubConnection | null = null;
    private notificationsSubject = new Subject<Notification>();

    /** Public observable for clients to subscribe to notifications. */
    notifications$: Observable<Notification> = this.notificationsSubject.asObservable();

    /** Callback invoked when the connection is lost. Set by consumers (e.g., ApiService). */
    onCloseCallback: (() => void) | null = null;

    /** Callback invoked when the connection is reconnecting. */
    onReconnectingCallback: ((error?: Error) => void) | null = null;

    /** Callback invoked when the connection is reconnected. */
    onReconnectedCallback: ((connectionId?: string) => void) | null = null;

    /** Flag indicating a software update is in progress — slows reconnect retries */
    private _updateInProgress = false;
    private _lastUrl: string | null = null;

    constructor() {
        // When a backgrounded tab becomes visible again, attempt to reconnect
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible' && this._lastUrl) {
                    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
                        console.log('[SignalR] Tab became visible, triggering reconnect');
                        if (!this.connection) {
                            this.connect(this._lastUrl).catch(err =>
                                console.error('[SignalR] Visibility reconnect failed:', err));
                        }
                        // If connection exists but disconnected, auto-reconnect should handle it
                    }
                }
            });
        }
    }

    /**
     * Hint to the reconnect policy that a software update is in progress.
     * Slows down initial retry delays to avoid reconnecting to the old process.
     */
    setUpdateInProgress(value: boolean): void {
        this._updateInProgress = value;
    }

    /**
     * Connect to the SignalR DashboardHub.
     * Converts the old WebSocket URL (ws://host:port/ws) to a SignalR hub URL (http://host:port/ws/hub).
     */
    async connect(url: string): Promise<void> {
        this._lastUrl = url;
        // Convert legacy ws:// URL to http:// SignalR hub URL
        let hubUrl = url;
        if (url.startsWith('ws://')) {
            hubUrl = url.replace(/^ws:\/\//, 'http://');
        } else if (url.startsWith('wss://')) {
            hubUrl = url.replace(/^wss:\/\//, 'https://');
        }
        // Append /hub if the URL ends with /ws
        if (hubUrl.endsWith('/ws')) {
            hubUrl = hubUrl + '/hub';
        }

        // Clean up any previous connection
        if (this.connection) {
            try { await this.connection.stop(); } catch { /* ignore */ }
            this.connection = null;
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // During updates, slow down to avoid reconnecting to the old process
                    if (this._updateInProgress) {
                        if (retryContext.previousRetryCount < 2) return 5000;
                        return 10000;
                    }
                    if (retryContext.previousRetryCount === 0) return 0;
                    if (retryContext.previousRetryCount === 1) return 1000;
                    if (retryContext.previousRetryCount === 2) return 2000;
                    if (retryContext.previousRetryCount === 3) return 5000;
                    return 10000;
                }
            })
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // Register the generic "Notification" handler for server-to-client push
        this.connection.on('Notification', (method: string, data: any) => {
            const notification: Notification = {
                jsonrpc: '2.0',
                method,
                params: data
            };
            this.notificationsSubject.next(notification);
        });

        // Wire up reconnect/close events for ApiService's setupConnectionMonitor
        this.connection.onreconnecting((error) => {
            console.log('[SignalR] Reconnecting...', error?.message);
            if (this.onReconnectingCallback) {
                this.onReconnectingCallback(error);
            }
        });

        this.connection.onreconnected((connectionId) => {
            console.log('[SignalR] Reconnected, connectionId:', connectionId);
            if (this.onReconnectedCallback) {
                this.onReconnectedCallback(connectionId);
            }
        });

        this.connection.onclose((error) => {
            console.log('[SignalR] Connection closed', error?.message);
            if (this.onCloseCallback) {
                this.onCloseCallback();
            }
        });

        await this.connection.start();
    }

    disconnect() {
        if (this.connection) {
            this.connection.stop().catch(() => { /* ignore */ });
            this.connection = null;
        }
    }

    /**
     * Force disconnect the SignalR connection and immediately fire the close callback.
     * Use this when you know the server is going down (reboot/restart) to ensure
     * the connection state is properly updated.
     */
    forceDisconnect() {
        console.log('[SignalR] Force disconnecting...');
        if (this.connection) {
            this.connection.stop().catch(() => { /* ignore */ });
            this.connection = null;
        }
        // Immediately fire the close callback since we know the connection is gone
        if (this.onCloseCallback) {
            this.onCloseCallback();
        }
    }

    /**
     * Check if the connection is currently connected.
     */
    isConnected(): boolean {
        return this.connection?.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Send a request to the hub via the generic Invoke method.
     * Replaces the JSON-RPC sendRequest pattern.
     * @param method The method name to invoke.
     * @param params Parameters for the method.
     * @param token Optional auth token.
     * @returns Promise resolving to the method result.
     */
    sendRequest(method: string, params?: any, token?: string | null): Promise<any> {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            return Promise.reject('SignalR is not connected.');
        }

        // Include token in the params if provided
        const argsWithToken = token ? { ...params, token } : params;

        console.debug(`[SignalR] Invoking '${method}'`, argsWithToken);
        return this.connection.invoke('Invoke', method, argsWithToken)
            .then(result => {
                console.debug(`[SignalR] '${method}' response:`, result);
                return result;
            })
            .catch(err => {
                console.error(`[SignalR] '${method}' failed:`, err);
                throw err;
            });
    }

    ngOnDestroy() {
        this.disconnect();
        this.notificationsSubject.complete();
    }
}
